/**
 * Render the workspace's declared MCP servers into the **coding agent's own**
 * native MCP config, so a developer who runs `skaile install` in a project and
 * then opens their coding agent in that folder gets the same MCP servers
 * skaile's own agent loop wires up.
 *
 * This is the sibling of skill deployment (`.claude/skills/`): skills already
 * render into the driver target's native tree, MCP servers now do too.
 *
 * Declarations come from the single resolver `loadMcpServerDeclarations()` in
 * `@skaile/workspaces/core` — the same one the runner uses. There is no second
 * resolver here.
 */

import fs from "node:fs/promises";
import path from "node:path";
import type { DriverTarget, McpServerDeclaration } from "@skaile/workspaces/core";
import { loadMcpServerDeclarations } from "@skaile/workspaces/core";

// ── Driver-target coverage ───────────────────────────────────────────────────

/**
 * Per-driver-target native MCP config location, relative to the project root.
 *
 * Only `claude-code` is populated: its project-scope `.mcp.json` schema is
 * documented and stable. `codex` (TOML `config.toml`) and `omp` (no documented
 * project-scope MCP file) are deliberately absent — an unlisted target is a
 * silent no-op, and adding one later is a one-line change here.
 */
const TARGET_MCP_CONFIG: Partial<Record<DriverTarget, string>> = {
  "claude-code": ".mcp.json",
};

/** IDs reserved for skaile's built-in MCP servers. Never rendered. */
const RESERVED_MCP_SERVER_IDS = new Set([
  "skaile-connectors",
  "skaile-workspace",
  "skaile-capabilities",
]);

/**
 * Secret-ref prefixes that cannot be represented in a coding agent's MCP
 * config. `env:` is the one exception — it maps onto the agent's own `${VAR}`
 * environment expansion (see {@link renderSecretRef}).
 *
 * Must stay in sync with `KNOWN_PREFIXES` in
 * `@skaile/workspaces/connectors` (`src/secrets.ts`) minus `env:`. Writing an
 * unresolved ref verbatim is exactly what `resolveRecordSecretsStrict` in
 * `runner/src/external-mcp.ts` refuses to do — it would put e.g.
 * `Authorization: mcp:github` on the wire and surface a misleading upstream
 * rejection instead of "the credential is unavailable".
 */
const UNSUPPORTED_SECRET_PREFIXES = [
  "vault:",
  "forge:",
  "op:",
  "kp:",
  "oauth:",
  "mount:",
  "connector:",
  "mcp:",
] as const;

/**
 * `${workspace}` marker, substituted with the absolute workspace root in
 * `command` / `args` / `env` — the same token the runner resolves.
 */
const WORKSPACE_MARKER = /\$\{workspace\}/g;

// ── Types ────────────────────────────────────────────────────────────────────

/** A skipped declaration and the human-readable reason it was not rendered. */
export interface SkippedMcpServer {
  id: string;
  reason: string;
}

/** Outcome of one {@link renderMcpServerConfig} pass. */
export interface RenderMcpConfigResult {
  /** Absolute path to the config file, when the target is supported. */
  path?: string;
  /** Whether the config file was actually written. */
  written: boolean;
  /** IDs skaile wrote (and now owns) in this pass. */
  servers: string[];
  /** Previously-owned IDs removed in this pass. */
  removed: string[];
  /** Declarations deliberately not rendered, with reasons. */
  skipped: SkippedMcpServer[];
}

/** Options for {@link renderMcpServerConfig}. */
export interface RenderMcpConfigOptions {
  /** Warning sink. Defaults to `console.warn` prefixed with `skaile:`. */
  warn?: (message: string) => void;
}

/** A rendered `.mcp.json` server entry (stdio or remote). */
interface McpConfigEntry {
  type?: "sse" | "http";
  command?: string;
  args?: string[];
  env?: Record<string, string>;
  url?: string;
  headers?: Record<string, string>;
}

interface McpJsonFile {
  mcpServers?: Record<string, unknown>;
  [k: string]: unknown;
}

interface ManagedState {
  version: number;
  targets: Record<string, string[]>;
}

// ── Value rendering ──────────────────────────────────────────────────────────

/**
 * Substitute `${workspace}` with the absolute project dir. A literal
 * `${workspace}` left in the file would be read by the coding agent as an
 * environment-variable expansion and silently resolve to nothing.
 */
function substituteWorkspace(value: string, projectDir: string): string {
  return value.replace(WORKSPACE_MARKER, projectDir);
}

/**
 * Map an `env:NAME` secret ref onto `${NAME}`, which the coding agent expands
 * from its own environment. Any other value passes through unchanged.
 */
function renderSecretRef(value: string): string {
  return value.startsWith("env:") ? `\${${value.slice(4)}}` : value;
}

/**
 * Render one `env` / `headers` map, or `undefined` when it is empty.
 *
 * `projectDir` is passed only for `env` (a local subprocess's environment);
 * `headers` are remote-transport config, where `${workspace}` is meaningless —
 * the same split the runner applies.
 */
function renderValueMap(
  map: Record<string, string> | undefined,
  projectDir?: string,
): Record<string, string> | undefined {
  if (!map) return undefined;
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(map)) {
    out[k] = renderSecretRef(projectDir ? substituteWorkspace(v, projectDir) : v);
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

/** First unsupported secret ref found across the declaration's value maps. */
function findUnsupportedSecretRef(decl: McpServerDeclaration): string | undefined {
  const values = [...Object.values(decl.env ?? {}), ...Object.values(decl.headers ?? {})];
  return values.find((v) => UNSUPPORTED_SECRET_PREFIXES.some((p) => v.startsWith(p)));
}

// ── Skip rules ───────────────────────────────────────────────────────────────

/**
 * Why this declaration cannot be rendered into a coding agent's MCP config, or
 * `undefined` when it can. Never attempts a Nix build or any other resolution.
 */
function skipReason(decl: McpServerDeclaration): string | undefined {
  if (RESERVED_MCP_SERVER_IDS.has(decl.id)) return "reserved id";

  // Mirrors the runner's back-compat default: an unset launcher with a recipe
  // binding means "recipe". Recipe resolution needs the platform's Nix runtime,
  // which a consumer machine does not have.
  if (decl.launcher === "recipe" || (decl.launcher == null && decl.recipe != null)) {
    return "requires the skaile platform's Nix recipe runtime";
  }

  if (decl.auth === "backend") return "requires a platform-minted credential";

  // A materialized/store-staged asset can have live plaintext credentials
  // folded into `env` from a sibling `.instance.json`. Those must never be
  // persisted to a file the project is told to commit — the same rule that
  // keeps `.instance.json` out of `.claude/skills/`.
  if (decl.instanceSecretsFolded) return "resolved credentials are folded in from .instance.json";

  const transport = decl.transport ?? "stdio";
  if (transport === "stdio" && !decl.command) return "no command";
  if (transport !== "stdio" && !decl.url) return "no url";

  const badRef = findUnsupportedSecretRef(decl);
  if (badRef) return `unsupported secret reference '${badRef}'`;

  return undefined;
}

// ── Entry building ───────────────────────────────────────────────────────────

/** Build the `.mcp.json` entry for a declaration that passed the skip rules. */
function buildEntry(decl: McpServerDeclaration, projectDir: string): McpConfigEntry {
  const transport = decl.transport ?? "stdio";
  if (transport !== "stdio") {
    const entry: McpConfigEntry = { type: transport, url: decl.url };
    const headers = renderValueMap(decl.headers);
    if (headers) entry.headers = headers;
    return entry;
  }

  // stdio is the documented default type and is omitted from the canonical
  // project-scope examples — omit it here too.
  const entry: McpConfigEntry = {
    command: substituteWorkspace(decl.command as string, projectDir),
  };
  const args = decl.args?.map((a) => substituteWorkspace(a, projectDir));
  if (args && args.length > 0) entry.args = args;
  const env = renderValueMap(decl.env, projectDir);
  if (env) entry.env = env;
  return entry;
}

// ── Managed-state I/O ────────────────────────────────────────────────────────

function managedStatePath(projectDir: string): string {
  return path.join(projectDir, ".skaile", "mcp-managed.json");
}

/**
 * Parse JSON at `p`, or `null` when the file is absent.
 *
 * @throws When the file exists but is unparseable — silently overwriting a
 *   hand-edited, version-controlled file would be worse than failing loudly.
 */
async function readJsonStrict<T>(p: string): Promise<T | null> {
  let raw: string;
  try {
    raw = await fs.readFile(p, "utf-8");
  } catch {
    return null;
  }
  try {
    return JSON.parse(raw) as T;
  } catch (e: unknown) {
    throw new Error(
      `cannot parse ${p}: ${e instanceof Error ? e.message : String(e)}; please fix or remove`,
    );
  }
}

async function writeJson(p: string, value: unknown): Promise<void> {
  await fs.mkdir(path.dirname(p), { recursive: true });
  await fs.writeFile(p, `${JSON.stringify(value, null, 2)}\n`);
}

async function readManagedIds(projectDir: string, target: DriverTarget): Promise<string[]> {
  const state = await readJsonStrict<ManagedState>(managedStatePath(projectDir));
  const ids = state?.targets?.[target];
  return Array.isArray(ids) ? ids : [];
}

async function writeManagedIds(
  projectDir: string,
  target: DriverTarget,
  ids: string[],
): Promise<void> {
  const p = managedStatePath(projectDir);
  const state = (await readJsonStrict<ManagedState>(p)) ?? { version: 1, targets: {} };
  const targets = { ...(state.targets ?? {}) };
  if (ids.length > 0) targets[target] = ids;
  else delete targets[target];
  await writeJson(p, { version: 1, targets });
}

// ── Merge ────────────────────────────────────────────────────────────────────

/** Result of merging renderable entries into an existing `mcpServers` map. */
interface MergeResult {
  next: Record<string, unknown>;
  owned: string[];
  removed: string[];
}

/**
 * Merge skaile-owned entries into the existing map without ever clobbering a
 * user-authored one. Ownership is explicit (the managed-id list), never
 * inferred from the entry's shape.
 */
function mergeServers(
  existing: Record<string, unknown>,
  renderable: Map<string, McpConfigEntry>,
  managedIds: string[],
  warn: (message: string) => void,
  configLabel: string,
): MergeResult {
  const next = { ...existing };
  const managed = new Set(managedIds);
  const owned: string[] = [];
  const removed: string[] = [];

  // Only previously-managed ids may be deleted. Never touch anything else.
  // `Object.hasOwn`, not `in` — `in` walks the prototype chain, so ids like
  // `constructor` / `toString` would read as pre-existing hand-written entries.
  for (const id of managed) {
    if (!renderable.has(id) && Object.hasOwn(next, id)) {
      delete next[id];
      removed.push(id);
    }
  }

  for (const [id, entry] of renderable) {
    if (Object.hasOwn(next, id) && !managed.has(id)) {
      warn(`"${id}" already declared in ${configLabel} by hand — leaving the existing entry`);
      continue;
    }
    next[id] = entry;
    owned.push(id);
  }

  return { next, owned, removed };
}

// ── Public API ───────────────────────────────────────────────────────────────

/**
 * Render every MCP server declared for `projectDir` into `driverTarget`'s
 * native project-scope MCP config.
 *
 * Merge rules, in short:
 * - Entries skaile previously wrote (tracked in `.skaile/mcp-managed.json`) are
 *   refreshed, or deleted once their declaration disappears.
 * - A hand-written entry with the same id is left untouched and warned about.
 * - Every other key of the config file survives verbatim.
 * - A project with no renderable servers and no prior state writes nothing.
 *
 * Unsupported driver targets (`codex`, `omp`) are a silent no-op.
 *
 * @param projectDir - Workspace root where `skaile.yaml` lives.
 * @param driverTarget - Coding agent whose native MCP config to write.
 * @param opts - Optional warning sink.
 * @returns What was written, removed and skipped.
 * @docLink packages/asset-manager/concepts#coding-agent-mcp-config
 */
export async function renderMcpServerConfig(
  projectDir: string,
  driverTarget: DriverTarget,
  opts?: RenderMcpConfigOptions,
): Promise<RenderMcpConfigResult> {
  const warn = opts?.warn ?? ((m: string) => console.warn(`skaile: ${m}`));

  const relConfig = TARGET_MCP_CONFIG[driverTarget];
  if (!relConfig) return { written: false, servers: [], removed: [], skipped: [] };

  const configPath = path.join(projectDir, relConfig);

  const declarations = await loadMcpServerDeclarations(projectDir);
  const skipped: SkippedMcpServer[] = [];
  const renderable = new Map<string, McpConfigEntry>();
  for (const decl of declarations) {
    const reason = skipReason(decl);
    if (reason) {
      skipped.push({ id: decl.id, reason });
      warn(`MCP server "${decl.id}" not written to ${relConfig}: ${reason}`);
      continue;
    }
    renderable.set(decl.id, buildEntry(decl, projectDir));
  }

  const managedIds = await readManagedIds(projectDir, driverTarget);
  const existingFile = await readJsonStrict<McpJsonFile>(configPath);

  // Nothing to write and nothing ever written: leave the project untouched
  // rather than creating an empty `.mcp.json` / `.skaile/` dir.
  if (renderable.size === 0 && managedIds.length === 0 && !existingFile) {
    return { path: configPath, written: false, servers: [], removed: [], skipped };
  }

  const file = existingFile ?? {};
  const existingServers = (file.mcpServers ?? {}) as Record<string, unknown>;
  const { next, owned, removed } = mergeServers(
    existingServers,
    renderable,
    managedIds,
    warn,
    relConfig,
  );

  // Claim ownership BEFORE writing the config. A crash between the two writes
  // must leave a ledger entry with no config entry (self-healing — the next
  // pass simply re-adds it), never a config entry with no ledger entry (which
  // would be misread as hand-written and disowned forever).
  const ledgerStale = JSON.stringify(owned) !== JSON.stringify(managedIds);
  if (ledgerStale) await writeManagedIds(projectDir, driverTarget, owned);

  // Never conjure an empty `.mcp.json` into a project that has none: with
  // nothing to write and no file to update, the ledger reconciliation above is
  // the whole job.
  if (Object.keys(next).length === 0 && !existingFile) {
    return { path: configPath, written: false, servers: owned, removed, skipped };
  }

  if (!ledgerStale && JSON.stringify(next) === JSON.stringify(existingServers) && existingFile) {
    return { path: configPath, written: false, servers: owned, removed, skipped };
  }

  await writeJson(configPath, { ...file, mcpServers: next });

  return { path: configPath, written: true, servers: owned, removed, skipped };
}
